import React from "react"
import { useFinanceDataContext } from "../contexts/FinanceDataContext"
import { useCurrency } from "../contexts/CurrencyContext"
import { useTheme } from "../contexts/ThemeContext"
import { formatSmartCurrency } from "../utils/formatCurrency"
import { useNavigate } from "react-router-dom"
import { useSidebar } from "../contexts/SidebarContext"
import moment from "moment"

const CurrentBalanceHeader = ({
    onBalancePress,
}) => {
    const {
        currentTotalBalance,
        hasAnyBalances,
        lastUpdateOrCreatedTime,
    } = useFinanceDataContext();

    const { currencyCode } = useCurrency();
    const { theme, mode, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const { toggleCollapsed, setMobileOpen } = useSidebar();

    const handleMenuClick = () => {
        const isMobile = window.innerWidth <= 768;
        if (isMobile) {
            setMobileOpen(true);
        } else {
            toggleCollapsed();
        }
    };

    const balanceDisplay = formatSmartCurrency(currentTotalBalance / 100, {
        mode: 'long',
        currencyCode,
    });

    const getLastUpdateText = () => {
        if (!lastUpdateOrCreatedTime) return '';
        return `Updated ${moment(lastUpdateOrCreatedTime).fromNow()}`;
    };

    const iconColor = theme.textColor;

    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', backgroundColor: theme.backgroundColor }}>
            {/* Left icons */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <IconBtn onClick={handleMenuClick} title="Menu" theme={theme}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
                    </svg>
                </IconBtn>
                <IconBtn onClick={() => {}} title="Chat" theme={theme}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                    </svg>
                </IconBtn>
            </div>

            {/* Center balance pill */}
            <div
                style={{
                    backgroundColor: theme.pillBg,
                    borderRadius: 20,
                    padding: '6px 20px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 2px 3px rgba(0,0,0,0.08)',
                    minWidth: 120,
                }}
                onClick={onBalancePress}
            >
                {hasAnyBalances ? (
                    <>
                        <div style={{ fontSize: 20, fontWeight: 800, color: theme.textColor, letterSpacing: -0.3 }}>{balanceDisplay}</div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: theme.tertiaryText, marginTop: 1 }}>{getLastUpdateText()}</div>
                    </>
                ) : (
                    <>
                        <div style={{ fontSize: 20, fontWeight: 800, color: theme.textColor, letterSpacing: -0.3 }}>$0.00</div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: theme.tertiaryText, marginTop: 1 }}>Add Account Balance</div>
                    </>
                )}
            </div>

            {/* Right icons */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <IconBtn onClick={() => navigate('/app/insights')} title="Insights" theme={theme}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
                    </svg>
                </IconBtn>
                <IconBtn onClick={toggleTheme} title={mode === 'dark' ? 'Light mode' : 'Dark mode'} theme={theme}>
                    {mode === 'dark' ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" /><line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" /><line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" /><line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                        </svg>
                    ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
                        </svg>
                    )}
                </IconBtn>
            </div>
        </div>
    );
};

const IconBtn = ({ onClick, children, title, theme }) => (
    <button
        onClick={onClick}
        title={title}
        style={{
            width: 40,
            height: 40,
            borderRadius: 20,
            border: 'none',
            backgroundColor: theme.iconButtonBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 3px rgba(0,0,0,0.08)',
            transition: 'background-color 0.15s',
            padding: 0,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = theme.iconButtonHover)}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = theme.iconButtonBg)}
    >
        {children}
    </button>
);

export default CurrentBalanceHeader
